import React from 'react';

import { usePathname, useSegments } from 'expo-router';

import { OnboardingScreensModal } from 'components/onboarding-screens-modal';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { OnboardingProvider } from './onboarding';

/** Routes that render as base content rather than over it: the tab group, plus the root screens
 * expo-router presents as cards. `menu/section/[id]` is declared that way in `src/app/_layout.tsx`
 * for its full-screen no-tabs look, and `checkout` inherits the default presentation. Every other
 * route group and root screen is presented as some flavour of modal. */
const BASE_CONTENT_ROUTE_SEGMENTS = new Set(['(tabs)', 'menu', 'checkout']);

export const MaybeShowOnboardingContent = () => {
  const { isUpdateModalOpen } = useUIContext();
  const { modalAuthIsOpen } = useAuthContext();
  const onboardingEnabled = useFlag(LaunchDarklyFlag.ENABLE_FEATURE_ONBOARDING);
  const pathname = usePathname();
  const segments = useSegments();

  // Onboarding content arrives asynchronously, so this can become renderable at any point in a
  // session — including while a store locator, cart step, drawer, campaign, or the auth drawer is
  // already on screen. Presenting this full-screen, non-dismissable modal on top of another
  // presented modal leaves an orphaned overlay that swallows every touch with no way out, which
  // reads as a frozen app. Allowlisting base content instead of listing the overlay routes keeps
  // new routes safe by default. The auth drawer is a React Native modal rather than a route, so the
  // route check cannot see it. See: https://rbictg.atlassian.net/browse/BKPE-24180
  const isOnBaseContent = BASE_CONTENT_ROUTE_SEGMENTS.has(segments[0] ?? '') && !modalAuthIsOpen;

  if (
    isUpdateModalOpen ||
    !isOnBaseContent ||
    !onboardingEnabled ||
    pathname.includes(routes.walmart)
  ) {
    return null;
  }

  return (
    <OnboardingProvider>
      <OnboardingScreensModal />
    </OnboardingProvider>
  );
};
